/**
 * Archive Service
 * Manages data lifecycle, archiving, and deletion
 * Keeps database lean by moving old data to archive storage
 */

import { DataService } from './DataService';
import type {
  ArchivedData,
  ContractItem,
  DataLifecyclePolicy,
  DataLifecycleLog,
  InterimApplication,
  Project,
  Tender,
} from '../types/database';
import type { Task } from '../types/tasks';

const DAYS_TO_MS = 24 * 60 * 60 * 1000;

// Default lifecycle policies
const DEFAULT_ARCHIVE_DAYS = 1460; // 4 years
const DEFAULT_DELETE_DAYS = 1825; // 5 years

class ArchiveServiceClass {
  /**
   * Get default lifecycle policy
   */
  public getDefaultPolicy(): DataLifecyclePolicy {
    return {
      id: 'default-policy',
      entityType: 'all',
      archiveAfterDays: DEFAULT_ARCHIVE_DAYS,
      deleteAfterDays: DEFAULT_DELETE_DAYS,
      enabled: true,
      autoArchive: true,
      autoDelete: false, // Require manual approval for deletion
      notifyBeforeArchive: true,
      notifyBeforeDelete: true,
      notifyDaysBefore: 30,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'system',
    };
  }

  /**
   * Check if an entity should be archived based on its age
   */
  public shouldArchive(createdAt: string, policy?: DataLifecyclePolicy): boolean {
    const archivePolicy = policy || this.getDefaultPolicy();
    if (!archivePolicy.enabled || !archivePolicy.autoArchive) return false;

    const ageInDays = this.getAgeInDays(createdAt);
    return ageInDays >= archivePolicy.archiveAfterDays;
  }

  /**
   * Check if an entity should be deleted based on its age
   */
  public shouldDelete(createdAt: string, policy?: DataLifecyclePolicy): boolean {
    const deletePolicy = policy || this.getDefaultPolicy();
    if (!deletePolicy.enabled || !deletePolicy.autoDelete) return false;

    const ageInDays = this.getAgeInDays(createdAt);
    return ageInDays >= deletePolicy.deleteAfterDays;
  }

  /**
   * Get age of entity in days
   */
  public getAgeInDays(createdAt: string): number {
    const created = new Date(createdAt).getTime();
    const now = Date.now();
    return Math.floor((now - created) / DAYS_TO_MS);
  }

  /**
   * Get days until archive
   */
  public getDaysUntilArchive(createdAt: string, policy?: DataLifecyclePolicy): number {
    const archivePolicy = policy || this.getDefaultPolicy();
    const ageInDays = this.getAgeInDays(createdAt);
    return Math.max(0, archivePolicy.archiveAfterDays - ageInDays);
  }

  /**
   * Get days until deletion
   */
  public getDaysUntilDeletion(createdAt: string, policy?: DataLifecyclePolicy): number {
    const deletePolicy = policy || this.getDefaultPolicy();
    const ageInDays = this.getAgeInDays(createdAt);
    return Math.max(0, deletePolicy.deleteAfterDays - ageInDays);
  }

  /**
   * Archive a project and all related data
   */
  public archiveProject(
    projectId: string,
    userId: string,
    reason: string = 'Automatic archival due to age'
  ): ArchivedData | null {
    const project = DataService.getProject(projectId);
    if (!project) return null;

    // Collect all related data
    const contractItems = DataService.getContractItemsByProject(projectId);
    const interimApps = DataService.getInterimApplicationsByProject(projectId);
    const retentionLedger = DataService.getRetentionLedgerByProject(projectId);
    const tasks = DataService.getTasksByProject(projectId);

    // Calculate total size (rough estimate)
    const relatedData = {
      project,
      contractItems,
      interimApps,
      retentionLedger,
      tasks,
    };
    const sizeInBytes = JSON.stringify(relatedData).length;

    // Create archived record
    const archived: ArchivedData = {
      id: `archive-${projectId}-${Date.now()}`,
      entityType: 'project',
      entityId: projectId,
      data: relatedData,
      archivedAt: new Date().toISOString(),
      archivedBy: userId,
      archiveReason: reason,
      originalCreatedAt: project.createdAt,
      deletionScheduledAt: new Date(Date.now() + (365 * DAYS_TO_MS)).toISOString(), // 1 year from now
      sizeInBytes,
      relatedEntitiesCount: contractItems.length + interimApps.length + tasks.length + (retentionLedger ? 1 : 0),
    };

    // Update project status
    DataService.updateProject(projectId, {
      archiveStatus: 'archived',
      archivedAt: archived.archivedAt,
      deletionScheduledAt: archived.deletionScheduledAt,
      archiveReason: reason,
      canRestore: true,
    });

    // Store in archive (this would be separate storage in production)
    // For now, we'll keep a reference in the database
    this.saveArchivedData(archived);

    // Log the action
    this.logAction({
      id: `log-${Date.now()}`,
      action: 'archived',
      entityType: 'project',
      entityId: projectId,
      entityName: project.name,
      performedBy: userId,
      performedAt: archived.archivedAt,
      details: `Archived project with ${archived.relatedEntitiesCount} related entities`,
      freedSpaceBytes: sizeInBytes,
    });

    console.log(`📦 Archived project ${project.name} (${projectId})`);
    console.log(`   Related entities: ${archived.relatedEntitiesCount}`);
    console.log(`   Size: ${this.formatBytes(sizeInBytes)}`);
    console.log(`   Scheduled deletion: ${this.formatDate(archived.deletionScheduledAt)}`);

    return archived;
  }

  /**
   * Restore an archived project
   */
  public restoreProject(archiveId: string, userId: string): boolean {
    const archived = this.getArchivedData(archiveId);
    if (!archived || archived.entityType !== 'project') return false;

    const projectId = archived.entityId;
    const project = DataService.getProject(projectId);
    
    if (!project || !project.canRestore) {
      console.error('❌ Cannot restore project - project not found or restore not allowed');
      return false;
    }

    // Restore project status
    DataService.updateProject(projectId, {
      archiveStatus: 'active',
      archivedAt: undefined,
      deletionScheduledAt: undefined,
      archiveReason: undefined,
      canRestore: true,
    });

    // Log the action
    this.logAction({
      id: `log-${Date.now()}`,
      action: 'restored',
      entityType: 'project',
      entityId: projectId,
      entityName: project.name,
      performedBy: userId,
      performedAt: new Date().toISOString(),
      details: `Restored project from archive`,
    });

    // Remove from archive storage
    this.removeArchivedData(archiveId);

    console.log(`♻️ Restored project ${project.name} (${projectId})`);
    return true;
  }

  /**
   * Permanently delete archived data
   */
  public async permanentlyDelete(archiveId: string, userId: string): Promise<boolean> {
    const archived = this.getArchivedData(archiveId);
    if (!archived) return false;

    const projectId = archived.entityId;
    const project = DataService.getProject(projectId);

    if (project) {
      // Delete project
      DataService.deleteProject(projectId);

      // Delete all related data
      const contractItems = DataService.getContractItemsByProject(projectId);
      contractItems.forEach((item: ContractItem) => DataService.deleteContractItem(item.id));

      const interimApps = DataService.getInterimApplicationsByProject(projectId);
      interimApps.forEach((app: InterimApplication) => DataService.deleteInterimApplication(app.id));

      const retentionLedger = DataService.getRetentionLedgerByProject(projectId);
      if (retentionLedger) {
        DataService.deleteRetentionLedger(retentionLedger.id);
      }

      const tasks = DataService.getTasksByProject(projectId);
      await Promise.all(tasks.map((task: Task) => DataService.deleteTask(task.id)));
    }

    // Log the action
    this.logAction({
      id: `log-${Date.now()}`,
      action: 'deleted',
      entityType: 'project',
      entityId: projectId,
      entityName: archived.data.project?.name || 'Unknown',
      performedBy: userId,
      performedAt: new Date().toISOString(),
      details: `Permanently deleted archived project`,
      freedSpaceBytes: archived.sizeInBytes,
    });

    // Remove from archive storage
    this.removeArchivedData(archiveId);

    console.log(`🗑️ Permanently deleted project (${projectId})`);
    console.log(`   Freed space: ${this.formatBytes(archived.sizeInBytes)}`);
    return true;
  }

  /**
   * Scan database for projects that need archiving
   */
  public scanForArchiveCandidates(policy?: DataLifecyclePolicy): Project[] {
    const projects = DataService.getProjects();
    const archivePolicy = policy || this.getDefaultPolicy();

    return projects.filter((project: Project) => {
      // Only archive completed or closed projects
      if (project.status === 'active' || project.archiveStatus !== 'active') {
        return false;
      }

      // Check if old enough to archive
      return this.shouldArchive(project.createdAt, archivePolicy);
    });
  }

  /**
   * Scan for archived data that should be deleted
   */
  public scanForDeletionCandidates(): ArchivedData[] {
    const archived = this.getAllArchivedData();
    const now = Date.now();

    return archived.filter(item => {
      const deletionTime = new Date(item.deletionScheduledAt).getTime();
      return now >= deletionTime;
    });
  }

  /**
   * Auto-archive old projects
   */
  public autoArchiveOldProjects(userId: string = 'system'): number {
    const candidates = this.scanForArchiveCandidates();
    let archivedCount = 0;

    candidates.forEach(project => {
      const result = this.archiveProject(
        project.id,
        userId,
        `Automatic archival - Project is ${this.getAgeInDays(project.createdAt)} days old`
      );
      if (result) archivedCount++;
    });

    if (archivedCount > 0) {
      console.log(`✅ Auto-archived ${archivedCount} projects`);
    }

    return archivedCount;
  }

  /**
   * Get storage statistics
   */
  public getStorageStats() {
    const projects = DataService.getProjects();
    const archived = this.getAllArchivedData();

    const activeProjects = projects.filter((p: Project) => p.archiveStatus === 'active');
    const archivedProjects = projects.filter((p: Project) => p.archiveStatus === 'archived');
    const pendingArchive = projects.filter((p: Project) => p.archiveStatus === 'pending-archive');

    // Calculate approximate sizes
    const activeSize = JSON.stringify(activeProjects).length;
    const archivedSize = archived.reduce((sum, item) => sum + item.sizeInBytes, 0);
    const totalSize = activeSize + archivedSize;

    return {
      totalProjects: projects.length,
      activeProjects: activeProjects.length,
      archivedProjects: archivedProjects.length,
      pendingArchive: pendingArchive.length,
      activeSize,
      archivedSize,
      totalSize,
      activeSizeFormatted: this.formatBytes(activeSize),
      archivedSizeFormatted: this.formatBytes(archivedSize),
      totalSizeFormatted: this.formatBytes(totalSize),
      spaceFreedByArchiving: this.formatBytes(archivedSize),
    };
  }

  /**
   * Get lifecycle logs
   */
  public getLogs(limit: number = 100): DataLifecycleLog[] {
    const logs = this.getAllLogs();
    return logs
      .sort((a, b) => new Date(b.performedAt).getTime() - new Date(a.performedAt).getTime())
      .slice(0, limit);
  }

  // ============================================================================
  // PRIVATE HELPER METHODS
  // ============================================================================

  private saveArchivedData(data: ArchivedData): void {
    const key = `archive_data_${data.id}`;
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving archived data:', error);
    }
  }

  private getArchivedData(archiveId: string): ArchivedData | null {
    const key = `archive_data_${archiveId}`;
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error getting archived data:', error);
      return null;
    }
  }

  private getAllArchivedData(): ArchivedData[] {
    const archived: ArchivedData[] = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('archive_data_')) {
          const data = localStorage.getItem(key);
          if (data) {
            archived.push(JSON.parse(data));
          }
        }
      }
    } catch (error) {
      console.error('Error getting all archived data:', error);
    }
    return archived;
  }

  private removeArchivedData(archiveId: string): void {
    const key = `archive_data_${archiveId}`;
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing archived data:', error);
    }
  }

  private logAction(log: DataLifecycleLog): void {
    const key = `archive_log_${log.id}`;
    try {
      localStorage.setItem(key, JSON.stringify(log));
    } catch (error) {
      console.error('Error saving log:', error);
    }
  }

  private getAllLogs(): DataLifecycleLog[] {
    const logs: DataLifecycleLog[] = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('archive_log_')) {
          const data = localStorage.getItem(key);
          if (data) {
            logs.push(JSON.parse(data));
          }
        }
      }
    } catch (error) {
      console.error('Error getting all logs:', error);
    }
    return logs;
  }

  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  private formatDate(isoDate: string): string {
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }
}

// Export singleton instance
export const ArchiveService = new ArchiveServiceClass();